import { Event, Group } from "@/db/schema";
import { updateEvent } from "@/lib/updateEvent";
import { combineCallbacks, createToastCallbacks, withCallbacks } from "@/utils/withCallbacks";
import React, { useActionState, useState } from "react";
import SubmitButton from "../common/SubmitButton";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { renderDynamicFields } from "../dynamic-fields/renderDynamicFields";

interface Props {
  event: Event;
  onCancel: () => void;
  groupDynamicFields?: Group["dynamicFields"];
}

export const EditEventForm: React.FC<Props> = ({ event, onCancel, groupDynamicFields }) => {
  const [startTime, setStartTime] = useState(new Date(event.startDate));
  const [endTime, setEndTime] = useState(new Date(event.endDate));
  const [dynamicFieldValues, setDynamicFieldValues] = useState<Record<string, string>>(
    event.dynamicFieldValues || {},
  );

  const [, formAction, isPending] = useActionState(
    withCallbacks(
      updateEvent,
      combineCallbacks(createToastCallbacks(), {
        onSuccess: onCancel,
      }),
    ),
    null,
  );

  const onChangeStartTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(":");
      const s = new Date(event.startDate);
      s.setHours(parseInt(hours), parseInt(minutes));

      // Validate if the resulting date is valid
      if (isNaN(s.getTime())) {
        throw new Error("Invalid date");
      }

      setStartTime(s);
    } catch (e) {
      console.error(e);
    }
  };

  const onChangeEndTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(":");
      const e = new Date(event.endDate);
      e.setHours(parseInt(hours), parseInt(minutes));

      // Validate if the resulting date is valid
      if (isNaN(e.getTime())) {
        throw new Error("Invalid date");
      }

      setEndTime(e);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDynamicFieldChange = (id: string, value: string) => {
    setDynamicFieldValues({
      ...dynamicFieldValues,
      [id]: value,
    });
  };

  return (
    <form action={formAction} className="flex flex-col gap-2 md:gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="name">Title</Label>
        <Input
          defaultValue={event.title}
          id="title"
          name="title"
          placeholder="ex. Morning meeting"
          className="col-span-3"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="authorName">Booked by</Label>
        <Input
          id="authorName"
          name="authorName"
          placeholder="ex. John Doe"
          className="col-span-3"
          defaultValue={event.authorName}
        />
      </div>
      {groupDynamicFields && groupDynamicFields.length > 0 && (
        <div className="space-y-2">
          {renderDynamicFields(groupDynamicFields, dynamicFieldValues, handleDynamicFieldChange)}
          <input
            type="hidden"
            name="dynamicFieldValues"
            value={JSON.stringify(dynamicFieldValues)}
          />
        </div>
      )}
      <div className="flex flex-col gap-2">
        <Label htmlFor="authorCompany">Company</Label>
        <Input
          id="authorCompany"
          name="authorCompany"
          placeholder="ex. E-corp"
          className="col-span-3"
          defaultValue={event.authorCompany}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-col flex-1 min-w-[120px] gap-2">
          <Label htmlFor="startTime">Start time</Label>
          <Input
            type="time"
            placeholder=""
            value={startTime.toTimeString().slice(0, 5)}
            className="col-span-3"
            onChange={(e) => {
              onChangeStartTime(e.target.value);
            }}
          />
          <input type="hidden" name="startTime" value={startTime.toISOString()} />
        </div>
        <div className="flex flex-col flex-1 min-w-[120px] gap-2">
          <Label htmlFor="endTime">End time</Label>
          <Input
            type="time"
            placeholder=""
            value={endTime.toTimeString().slice(0, 5)}
            className="col-span-3"
            onChange={(e) => {
              const value = e.target.value;
              if (!value || !/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
                onChangeEndTime("");
              } else {
                onChangeEndTime(value);
              }
            }}
          />
          <input type="hidden" name="endTime" value={endTime.toISOString()} />
        </div>
      </div>
      <div className="flex flex-row gap-1">
        <Button
          variant="accent"
          type="button"
          onClick={() => {
            setEndTime(new Date(startTime.getTime() + 30 * 60 * 1000));
          }}
        >
          30min
        </Button>
        <Button
          variant="accent"
          type="button"
          onClick={() => {
            setEndTime(new Date(startTime.getTime() + 60 * 60 * 1000));
          }}
        >
          1h
        </Button>
        <Button
          variant="accent"
          type="button"
          onClick={() => {
            setEndTime(new Date(startTime.getTime() + 90 * 60 * 1000));
          }}
        >
          1h 30min
        </Button>
        <Button
          variant="accent"
          type="button"
          onClick={() => {
            setEndTime(new Date(startTime.getTime() + 120 * 60 * 1000));
          }}
        >
          2h
        </Button>
      </div>
      <input type="hidden" name="resourceId" value={event.resourceId} />
      <input type="hidden" name="date" value={startTime.toISOString()} />
      <input type="hidden" name="eventId" value={event.id} />

      <div className="flex flex-row items-center space-x-2 justify-end">
        <Button variant={"outline"} type="button" onClick={onCancel}>
          Cancel
        </Button>
        <SubmitButton text="Save" isPending={isPending} />
      </div>
    </form>
  );
};
